import {
  wppQuotationDefaultWppText,
  wppQuotationTextFormat,
} from '@/components/Movements/WorkOrders/Details/WorkOrderFooter/ComplementWhatsappButton/ComplementWhatsappModal/Options';
import { wppTags } from '@/components/Movements/WorkOrders/Details/WorkOrderFooter/ComplementWhatsappButton/options';
import { FloatingFormItem, Group } from '@/components/_common';
import TextareaVars from '@/components/_common/TextareaVars';
import { ContactType } from '@/core/domain/_shared/ContactType';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import { useSupplier } from '@/core/domain/people/Suppliers/Suppliers.hooks';
import { useDefaultEmptyFieldError, useWindowSize } from '@/hooks';
import { MatrixPartnerOrigin } from '@/types/Register';
import notificationError from '@/utils/functions/notificationError';
import { applyMask, removeMask } from '@/utils/mask';
import { cellphoneMask } from '@/utils/mask/commonMasks';
import {
  Alert,
  AutoComplete,
  Button,
  Form,
  Modal,
  ModalProps,
  Space,
  Typography,
} from 'antd';
import { useCallback, useEffect } from 'react';

import { FaExternalLinkAlt } from 'react-icons/fa';

interface Props extends ModalProps {
  supplierId?: number;
  quotationCode?: string;
  onCancel?: VoidFunction;
}

const QuotationWhatsappModal = ({
  open,
  onCancel,
  supplierId,
  quotationCode,
  ...props
}: Props) => {
  const [form] = Form.useForm<{
    number: string;
    wppText: string;
  }>();

  const { isMobile } = useWindowSize();

  const wppText = Form.useWatch('wppText', form);

  const { data, dataUpdatedAt } = useSupplier(supplierId);

  const contacts = data?.contactList
    .map((a) => a.detailList)
    .flat()
    .filter((contact) => contact.contactType === ContactType.WhatsApp);

  const quotationUrl = `${window.location.origin}/external/quotation/${quotationCode}`;

  const requiredField = useDefaultEmptyFieldError();

  const { data: matrix } = useMatrix();

  const filterTagsForBosch = useCallback(
    (tag: string) => {
      const isBoschOrigin =
        !!matrix?.partnerOrigin &&
        [
          MatrixPartnerOrigin.BOSCH_CAR_SERVICE,
          MatrixPartnerOrigin.BOSCH_MODULO,
        ].includes(matrix?.partnerOrigin);

      if (!isBoschOrigin && tag === '{{boschCoReURL}}') return false;

      return true;
    },
    [matrix?.partnerOrigin],
  );

  const tags = Object.keys(wppTags)
    .filter(filterTagsForBosch)
    .map((tag) => ({
      value: tag,
      label: wppTags[tag],
    }));

  async function onSubmit() {
    try {
      const values = await form.validateFields();

      const number = removeMask(values.number);

      sendMessage(number);
    } catch (error) {
      notificationError(error);
    }
  }

  function sendMessage(number: string) {
    const textToSend = `${wppQuotationTextFormat({
      wppText,
      quotationUrl,
      supplierName: data?.name,
    })}`;

    const urlQueryString = new URLSearchParams({
      phone: `55${number}`,
      text: textToSend,
    }).toString();

    if (isMobile) {
      window.open(
        `https://wa.me/55${number}?text=${encodeURIComponent(textToSend)}`,
        '_blank',
      );
    } else {
      window.open(`https://web.whatsapp.com/send?${urlQueryString}`, '_blank');
    }

    if (onCancel) {
      onCancel();
    }
  }

  useEffect(() => {
    if (contacts?.length) {
      form.setFieldValue('number', contacts[0].value);
    }
  }, [dataUpdatedAt]);

  return (
    <Modal
      {...props}
      destroyOnClose
      open={open}
      onCancel={onCancel}
      onOk={onSubmit}
      okText="Enviar"
      title="Cotação por WhatsApp"
      okButtonProps={{
        icon: <FaExternalLinkAlt />,
      }}
    >
      <Form form={form} preserve={false}>
        <Space direction="vertical">
          <Typography.Text>
            Para enviar o link da cotação, preencha o número do WhatsApp do
            cliente.
          </Typography.Text>

          <FloatingFormItem
            name="number"
            label="Número do fornecedor"
            getValueFromEvent={(value) => {
              if (value.length < 11) {
                return removeMask(value);
              }
              return applyMask(removeMask(value), cellphoneMask);
            }}
            rules={[requiredField]}
          >
            <AutoComplete
              options={contacts?.map((contact) => ({
                value: contact.value,
                label: applyMask(removeMask(contact.value), cellphoneMask),
              }))}
            />
          </FloatingFormItem>

          {!contacts?.length && (
            <Alert
              style={{ marginBottom: 24 }}
              type="warning"
              description={
                <>
                  <div style={{ marginBottom: 16 }}>
                    Não há WhatsApp cadastrado. É necessário ter pelo menos um
                    número de WhatsApp nos contatos do fornecedor ou digitar
                    diretamente no campo acima.
                  </div>

                  <Group>
                    <Button
                      type="link"
                      onClick={() =>
                        window.open(
                          `/app/registry/suppliers/${supplierId}/contact`,
                          '_blank',
                        )
                      }
                    >
                      Ir para configuração de contato do fornecedor
                    </Button>
                  </Group>
                </>
              }
            />
          )}

          <Typography.Text>
            Colocar textos entre (*) deixará o texto em negrito no WhatsApp.
          </Typography.Text>

          <Form.Item
            name="wppText"
            noStyle
            initialValue={wppQuotationDefaultWppText}
          >
            <TextareaVars
              selectProps={{ disabled: true }}
              tags={tags}
              rows={10}
            />
          </Form.Item>

          <Alert
            type="info"
            description={
              <div
                style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  wordWrap: 'break-word',
                }}
                dangerouslySetInnerHTML={{
                  __html: `${wppQuotationTextFormat({
                    wppText,
                    quotationUrl,
                    supplierName: data?.name,
                  })}`,
                }}
              />
            }
          />
        </Space>
      </Form>
    </Modal>
  );
};

export default QuotationWhatsappModal;
